import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Palette, Loader2, Check } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { showToast } from '@/components/ui/Toast';

interface Option {
    id: number;
    name: string;
}

interface ExtraCurricularData {
    selected: {
        sports: number[];
        activities: number[];
        labs: number[];
        medical: number[];
    };
    options: {
        sports: Option[];
        activities: Option[];
        labs: Option[];
        medical: Option[];
    };
}

const ExtraCurricular: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<ExtraCurricularData | null>(null);
    const [selectedSports, setSelectedSports] = useState<number[]>([]);
    const [selectedActivities, setSelectedActivities] = useState<number[]>([]);
    const [selectedLabs, setSelectedLabs] = useState<number[]>([]);
    const [selectedMedical, setSelectedMedical] = useState<number[]>([]);

    useEffect(() => {
        fetchExtraCurricularData();
    }, [id]);

    const fetchExtraCurricularData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiRequest(`/extra-curricular/${id}`);
            if (response.success && response.data) {
                setData(response.data);
                setSelectedSports(response.data.selected.sports || []);
                setSelectedActivities(response.data.selected.activities || []);
                setSelectedLabs(response.data.selected.labs || []);
                setSelectedMedical(response.data.selected.medical || []);
            } else {
                setError(response.message || 'Failed to load extra curricular information');
            }
        } catch (err: any) {
            console.error('Error fetching extra curricular data:', err);
            setError('Failed to load extra curricular information');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!data) return;

        try {
            setSaving(true);
            setError(null);

            const payload = {
                sports: selectedSports,
                activities: selectedActivities,
                labs: selectedLabs,
                medical: selectedMedical,
            };

            const response = await apiRequest(`/extra-curricular/${id}`, {
                method: 'PUT',
                body: payload,
            });

            if (response.success) {
                showToast('Extra curricular information updated successfully!', 'success');
            } else {
                throw new Error(response.message || 'Failed to update');
            }
        } catch (err: any) {
            console.error('Error saving extra curricular data:', err);
            const errorMessage = err.message || 'Failed to save extra curricular information';
            setError(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setSaving(false);
        }
    };

    const toggleSelection = (
        optionId: number,
        currentSelection: number[],
        setSelection: React.Dispatch<React.SetStateAction<number[]>>
    ) => {
        if (currentSelection.includes(optionId)) {
            setSelection(currentSelection.filter(id => id !== optionId));
        } else {
            setSelection([...currentSelection, optionId]);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 text-brandColor animate-spin" />
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="p-8">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-red-800 dark:text-red-200">{error}</p>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="p-8">
                <p className="text-gray-500">No data available</p>
            </div>
        );
    }

    return (
        <div>
            {/* Header - Fixed */}
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-8 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                            <div className="p-2 bg-brandColor rounded-lg text-white">
                                <Palette className="w-6 h-6" />
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Sports and Facilities</h1>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Manage sports, activities, labs, and medical facilities</p>
                        </div>
                    </div>
                    <div className="flex-shrink-0">
                        <button
                            type="submit"
                            form="extra-curricular-form"
                            disabled={saving}
                            className="inline-flex items-center px-4 py-2 bg-brandColor hover:bg-brand-hover text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandColor text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="mx-8 mt-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-red-800 dark:text-red-200">{error}</p>
                </div>
            )}

            {/* Form */}
            <form id="extra-curricular-form" onSubmit={handleSubmit} className="p-8 space-y-6">
                {/* Sports */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-brandColor px-6 py-3">
                        <h2 className="text-lg font-semibold text-white">Sports</h2>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {data.options.sports.map((sport) => (
                                <label
                                    key={sport.id}
                                    className="flex items-center space-x-2 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedSports.includes(sport.id)}
                                        onChange={() => toggleSelection(sport.id, selectedSports, setSelectedSports)}
                                        className="w-4 h-4 text-brandColor bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-brandColor"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">{sport.name}</span>
                                    {selectedSports.includes(sport.id) && (
                                        <Check className="w-4 h-4 text-brandColor ml-auto" />
                                    )}
                                </label>
                            ))}
                        </div>
                        {data.options.sports.length === 0 && (
                            <p className="text-gray-500 dark:text-gray-400 text-sm">No sports options available</p>
                        )}
                    </div>
                </div>

                {/* Extra Activities */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-brandColor px-6 py-3">
                        <h2 className="text-lg font-semibold text-white">Extra Activities</h2>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {data.options.activities.map((activity) => (
                                <label
                                    key={activity.id}
                                    className="flex items-center space-x-2 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedActivities.includes(activity.id)}
                                        onChange={() => toggleSelection(activity.id, selectedActivities, setSelectedActivities)}
                                        className="w-4 h-4 text-brandColor bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-brandColor"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">{activity.name}</span>
                                    {selectedActivities.includes(activity.id) && (
                                        <Check className="w-4 h-4 text-brandColor ml-auto" />
                                    )}
                                </label>
                            ))}
                        </div>
                        {data.options.activities.length === 0 && (
                            <p className="text-gray-500 dark:text-gray-400 text-sm">No activity options available</p>
                        )}
                    </div>
                </div>

                {/* Labs */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-brandColor px-6 py-3">
                        <h2 className="text-lg font-semibold text-white">Labs</h2>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {data.options.labs.map((lab) => (
                                <label
                                    key={lab.id}
                                    className="flex items-center space-x-2 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedLabs.includes(lab.id)}
                                        onChange={() => toggleSelection(lab.id, selectedLabs, setSelectedLabs)}
                                        className="w-4 h-4 text-brandColor bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-brandColor"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">{lab.name}</span>
                                    {selectedLabs.includes(lab.id) && (
                                        <Check className="w-4 h-4 text-brandColor ml-auto" />
                                    )}
                                </label>
                            ))}
                        </div>
                        {data.options.labs.length === 0 && (
                            <p className="text-gray-500 dark:text-gray-400 text-sm">No lab options available</p>
                        )}
                    </div>
                </div>

                {/* Medical Facilities */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-brandColor px-6 py-3">
                        <h2 className="text-lg font-semibold text-white">Medical Facilities</h2>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {data.options.medical.map((facility) => (
                                <label
                                    key={facility.id}
                                    className="flex items-center space-x-2 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedMedical.includes(facility.id)}
                                        onChange={() => toggleSelection(facility.id, selectedMedical, setSelectedMedical)}
                                        className="w-4 h-4 text-brandColor bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-brandColor"
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-300">{facility.name}</span>
                                    {selectedMedical.includes(facility.id) && (
                                        <Check className="w-4 h-4 text-brandColor ml-auto" />
                                    )}
                                </label>
                            ))}
                        </div>
                        {data.options.medical.length === 0 && (
                            <p className="text-gray-500 dark:text-gray-400 text-sm">No medical facility options available</p>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
};

export default ExtraCurricular;
